/**
 * What a task row IS — the vocabulary, and the one place that decides which
 * members count as work.
 *
 * ## The defect this closes
 *
 * A plan is imported and every entry becomes a task. Some of those entries are
 * project CONTENT rather than steps — "Axis: prompt size, from a few hundred
 * characters to the server cap", "Rule: no behaviour fix ships without its own
 * persona and judge pair" — and a `TrackTask` had no field saying so. Nothing
 * distinguished them from "Extract the app-builder lifecycle skeleton", so a
 * decision or a definition sat in the roll-up denominator forever and the board
 * reported outstanding work that nobody would ever tick. Measured on one live
 * track: 136 rows, of which 45 of the 107 simultaneously-ready rows were
 * statements rather than steps.
 *
 * ## A CLOSED enum, never a free string
 *
 * The roll-up PARTITIONS on this value, and a partition over an unbounded
 * vocabulary cannot be exhaustively handled — the same reason this codebase
 * requires `Record<EnumType, …>` for enum-to-UI maps rather than an `if`/`else`
 * chain with a silent fallback. {@link TASK_KIND_COUNTS_AS_WORK} is that
 * `Record`, so a fourth member cannot land without a compile error at the one
 * line that decides what it means.
 *
 * ## `STEP` is the default everywhere, and that is what protects every caller
 *
 * The column is `NOT NULL DEFAULT 'STEP'`, the import path defaults to `STEP`,
 * and every reader here treats a missing or `null` kind as `STEP`. So a row
 * written before the column existed, a caller that never names a kind, and a
 * payload that lost the field crossing a wire all mean exactly what they meant
 * before: work. The behaviour of this product changes only for rows somebody
 * deliberately reclassified.
 */

/**
 * The vocabulary, in the order a reader should meet it: the default first.
 *
 * 🔴 THIS IS THE HAND-WRITTEN STATEMENT OF THE VOCABULARY, AND IT IS
 * DELIBERATELY NOT DERIVED FROM `DbEnum.TrackTaskKind`.
 * `prisma-enum-parity.test.ts` pairs the exported contract enum against the
 * `TrackTaskKind` declared in `schema.prisma` and compares the two value sets;
 * deriving this from the generated mirror would make that gate compare a mirror
 * with itself — DRYer, and a tautology. `TrackTaskKindSchema` in
 * `api/public/v1/schemas/tracks.schemas.ts` is `z.enum(TRACK_TASK_KINDS)`, which
 * keeps ONE hand-written vocabulary in this package rather than two, while
 * leaving the database side an independent statement to be checked against.
 */
export const TRACK_TASK_KINDS = ["STEP", "DECISION", "DEFINITION"] as const;

export type TrackTaskKind = (typeof TRACK_TASK_KINDS)[number];

/**
 * What a row is when nobody said.
 *
 * Named rather than spelled `"STEP"` at each site, because the whole
 * backward-compatibility argument is that every one of those sites agrees.
 */
export const DEFAULT_TRACK_TASK_KIND: TrackTaskKind = "STEP";

/**
 * Which kinds are outstanding WORK.
 *
 * 🔴 A `Record` RATHER THAN A `!== "STEP"` TEST, SO A NEW MEMBER CANNOT INHERIT
 * AN ANSWER. Adding a fourth kind to {@link TRACK_TASK_KINDS} without adding it
 * here is a compile error at this line; a negated equality would have silently
 * counted it, and a roll-up counting the wrong rows is the class of wrong number
 * that stays plausible and is never noticed.
 */
export const TASK_KIND_COUNTS_AS_WORK: Readonly<Record<TrackTaskKind, boolean>> = {
  STEP: true,
  DECISION: false,
  DEFINITION: false
};

/**
 * The kinds that ARE work, derived from {@link TASK_KIND_COUNTS_AS_WORK} rather
 * than listed again.
 *
 * 🔴 IT EXISTS SO A SQL PREDICATE CAN BE BOUND FROM IT RATHER THAN SPELLING
 * `'STEP'` INTO A STATEMENT. The per-track ready set filters its CANDIDATE rows
 * on this array, and a hand-typed literal there would be a second statement of
 * the vocabulary in a language `tsc` cannot read — so a fourth kind would change
 * the roll-up and leave the ready set silently offering content as work.
 *
 * ⚠️ BIND IT, NEVER SPLICE IT. `${TRACK_TASK_WORK_KINDS}` as a bound parameter is
 * a value; a `Prisma.sql` fragment interpolated into the statement is a splice,
 * and this domain's own record is that one splice turned a tenancy tie off and
 * dropped the whole statement out of the anchored-read scan.
 *
 * 🔴 THIS ARRAY AND {@link normalizeTaskKind} RESOLVE AN UNRECOGNISED KIND IN
 * OPPOSITE DIRECTIONS, AND THAT IS DELIBERATE — DO NOT "UNIFY" THEM.
 * `normalizeTaskKind` resolves an unknown value to `STEP`, so the roll-up COUNTS
 * it; this array does not contain it, so the ready set's `work` CTE EXCLUDES it.
 * Each half fails safe for the job it does, and they are different jobs:
 *
 * - the roll-up is a COUNTER, and a counter that cannot classify must not
 *   silently drop a row from a denominator — it fails toward counting, which is
 *   visible;
 * - the ready set is a RELEASER, and `work` is read on BOTH sides of its
 *   anti-join. Widening it would let an unknown-kind blocker enter the
 *   structure-escape clause and release its dependents, which is the one
 *   direction that hands out genuinely blocked work.
 *
 * So making the SQL fall back to "work" would break the ready set's
 * direction-of-error rule, and making `normalizeTaskKind` exclude would delete
 * real work from a denominator and break the null/undefined compatibility
 * guarantee above. Both unifications are worse than the split.
 *
 * ⚠️ THE DIVERGENCE IS UNREACHABLE IN A RELEASED BUILD AND CANNOT BE TESTED.
 * `TrackTask.kind` is a Postgres enum, and `prisma-enum-parity.test.ts` pins
 * `schema.prisma`'s members against {@link TRACK_TASK_KINDS} — so a row the build
 * cannot classify exists only between an enum-adding migration and the deploy
 * that knows the member, i.e. against pods mid-rolling-deploy. A fixture would
 * have to `ALTER TYPE` the enum in a shared test database to construct one, so
 * the behaviour is stated here rather than pinned.
 */
export const TRACK_TASK_WORK_KINDS: readonly TrackTaskKind[] = TRACK_TASK_KINDS.filter(
  (kind) => TASK_KIND_COUNTS_AS_WORK[kind]
);

/**
 * What an unset, absent or unrecognised kind MEANS. The one fallback in this
 * module, so nothing else has to spell it.
 *
 * ⚠️ `undefined` AND `null` BOTH RESOLVE TO {@link DEFAULT_TRACK_TASK_KIND}, AND
 * THAT IS THE COMPATIBILITY GUARANTEE RATHER THAN LENIENCE. A row read through a
 * projection that predates the column, and a JSON payload that lost a field
 * crossing a wire, both arrive here without a kind — and the honest answer for
 * both is what the row meant before the column existed. Resolving them to
 * anything else would delete real work from a denominator.
 *
 * ⚠️ AN UNRECOGNISED STRING RESOLVES THE SAME WAY, for the same reason one layer
 * out: a value the database grew and this build has not is a row this code
 * cannot classify, and a classifier that cannot classify must not silently
 * exclude. It fails toward counting, which is visible, rather than toward
 * omitting, which is not.
 */
export const normalizeTaskKind = (kind: string | null | undefined): TrackTaskKind =>
  kind != null && (TRACK_TASK_KINDS as readonly string[]).includes(kind)
    ? (kind as TrackTaskKind)
    : DEFAULT_TRACK_TASK_KIND;

/** Does this row count as outstanding work? */
export const taskKindCountsAsWork = (kind: string | null | undefined): boolean =>
  TASK_KIND_COUNTS_AS_WORK[normalizeTaskKind(kind)];
